import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CheckCircleOutlineIcon from '@atlaskit/icon/glyph/check-circle-outline';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import {
  MarieSchemaAnalytics,
  SanitizedFormattedHtmlMessage
} from '@atlassiansox/bxpkit-core';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  useAuthenticationMethodsCheck,
  useDebounce,
  useEmailValidation
} from '../../../../../hook';
import {
  getTranslatableError,
  messages
} from '../../../../../messages/sign-up-form';
import { EmailFieldPropsType } from '../../../../../model/email-field';
import { useAppSelector } from '../../../../../redux';
import {
  ChooseSignupLabel,
  ChooseSignupSuccessTextfieldWrapper,
  EmailAfter,
  ErrorMessageWithDefaultClassName,
  FieldContainer,
  Label,
  LabelDefaultExpandedEmail,
  SigninWithDiffAccount,
  SuccessTextfieldWrapper
} from './styled';

export function EmailField({
  onEmailChange,
  initialEmail,
  signInAccountUrl,
  switchAccountUrl,
  originAnalyticsAttributes,
  analyticsTrack,
  wacEnvUrl,
  isStaging,
  isDisabled,
  outlineOnFocus,
  fadeOnDisable,
  popOverError,
  showWarningIcon,
  onSSOCheckValid,
  isDeferredSiteNameEnabled,
  isFocusedChooseSignup,
  setIsFocusedChooseSignup,
  showError,
  setShowError,
  setN2NSignInRedirect,
  isConfluence
}: EmailFieldPropsType) {
  const { enableDefaultExpandedEmail } = useAppSelector(
    (state) => state.featureFlags
  );

  const { segment } = analyticsTrack;
  const baseUrl = `${wacEnvUrl}${isStaging ? '/apis/stg' : ''}/hamlet`;
  const hasInitialEmail = (initialEmail && initialEmail !== '') || false;
  const [alreadySentValid, setAlreadySentValid] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [email, setEmail] = useState(initialEmail ? initialEmail : '');

  const { setUserEmail } = useContext(MarieSchemaAnalytics);
  const { isValid, message, isValidating } = useEmailValidation({
    email: useDebounce(email, 125),
    baseUrl,
    hasInitialEmail,
    isConfluence
  });

  const { createAnalyticsEvent } = useAnalyticsEvents();

  const emailExistsOrSSOAnalytics = () => {
    createAnalyticsEvent({
      eventName: 'wac-signup-existing-user-signin',
      eventComponent: 'button',
      event: 'clicked'
    }).fire();
  };

  const { ssoRedirectUrl, isCheckingSSO, isUserExists } =
    useAuthenticationMethodsCheck({
      email: useDebounce(email, 125),
      allowAuthenticationsMethodCheck: isValid && !hasInitialEmail
    });
  useEffect(() => {
    setN2NSignInRedirect(ssoRedirectUrl);
  }, [ssoRedirectUrl]);
  const removeSpaces = (str: string) => {
    return str.replace(/\s/g, '');
  };

  const buildErrorMessage = (ssoRedirectUrl: string) => {
    if (ssoRedirectUrl === 'error') {
      return (
        <SanitizedFormattedHtmlMessage {...messages.warningNetworkError} />
      );
    } else if (ssoRedirectUrl === 'exists') {
      return (
        <SanitizedFormattedHtmlMessage
          {...messages.errorEmailInUseMsg}
          values={{
            signInAccountUrl: `${signInAccountUrl}&email=${encodeURIComponent(
              email
            )}`
          }}
        />
      );
    } else if (isUserExists && ssoRedirectUrl.includes('/saml?')) {
      return (
        <SanitizedFormattedHtmlMessage
          {...messages.ssoCheck}
          values={{ redirectUrl: ssoRedirectUrl }}
        />
      );
    }
    return undefined;
  };

  if (!isValid && message) {
    // TODO: tracking and analytics, bxp.formCloudSignup.emailValidationFailure
  }
  const handleSignInUserAccount = (event: any) => {
    if (!event.target || !event.target.href) {
      return;
    }

    event.preventDefault();
    if (signInAccountUrl) {
      const signInOriginAttributes = originAnalyticsAttributes.signInUser || {};
      segment('wac-signup-existing-user-signin', signInOriginAttributes);

      window.location.assign(signInAccountUrl);
    } else {
      segment('wac-signup-existing-user-signin-error', {});
    }
  };

  const handleSwitchUserAccount = (event: any) => {
    if (!event.target || !event.target.href) {
      return;
    }

    event.preventDefault();
    if (switchAccountUrl) {
      const switchUserOriginAttributes =
        originAnalyticsAttributes.switchUser || {};
      segment('wac-signup-existing-user-switch', switchUserOriginAttributes);

      window.location.assign(switchAccountUrl);
    } else {
      segment('wac-signup-existing-user-switch-error', {});
    }
  };

  useEffect(() => {
    if (isUserExists || isCheckingSSO) {
      onSSOCheckValid(true);
    } else {
      onSSOCheckValid(false);
    }
  }, [isCheckingSSO]);

  useEffect(() => {
    !isValidating && !!isValid ? onEmailChange(email) : onEmailChange('');

    if (!isValidating && !!isValid && !alreadySentValid) {
      // marie schema hash email for context
      setUserEmail(email);
      setShowError(true);
      //
      setAlreadySentValid(true);
    }
  }, [email, isValid]);

  return (
    <FieldContainer className="email-field sign-up-field-container">
      <Label
        as={
          enableDefaultExpandedEmail
            ? LabelDefaultExpandedEmail
            : isConfluence
            ? ChooseSignupLabel
            : Label
        }
        isDisabled={isDisabled}
        fadeOnDisable={fadeOnDisable}
        htmlFor="email-field-input"
      >
        <FormattedMessage {...messages.email} />
      </Label>
      {isConfluence ? (
        <ChooseSignupSuccessTextfieldWrapper
          textInput={email}
          isValid={!!isValid && !isUserExists && !isCheckingSSO}
          hasAccount={initialEmail !== ''}
          isEmpty={email.length === 0}
          isFocused={isFocusedChooseSignup}
          isDisabled={isDisabled}
          fadeOnDisable={fadeOnDisable}
          outlineOnFocus={outlineOnFocus}
        >
          <Textfield
            className="email-field input"
            id="email-field-input"
            testId="email_field"
            isRequired={!isConfluence}
            value={email}
            isInvalid={
              !isFocusedChooseSignup &&
              showError &&
              isUserExists &&
              isCheckingSSO &&
              isValid === false
            }
            style={{
              height: isConfluence ? '320px' : 'inherit',
              color: '#091E44',
              padding: '8px 10px'
            }}
            autoCorrect="off"
            autoCapitalize="none"
            autoComplete="email"
            isDisabled={hasInitialEmail || isDisabled}
            onFocus={() => {
              setIsFocusedChooseSignup(true);
              setShowError(false);
            }}
            onBlur={() => {
              setIsFocusedChooseSignup(false);
              setShowError(true);
            }}
            onChange={(event: any) => {
              setEmail(removeSpaces(event.currentTarget.value));
            }}
            elemAfterInput={
              <EmailAfter>
                {!isCheckingSSO && initialEmail === '' && isValid ? (
                  <CheckCircleOutlineIcon
                    testId="email_success"
                    label="success"
                    primaryColor="#36B37E"
                  />
                ) : (isValid === false &&
                    showError &&
                    !isFocusedChooseSignup) ||
                  isUserExists ? (
                  showWarningIcon && (
                    <WarningIcon
                      testId="email_warning"
                      label="success"
                      primaryColor="#DE350B"
                    />
                  )
                ) : isValidating || isCheckingSSO ? (
                  <Spinner size="small" />
                ) : null}
              </EmailAfter>
            }
          />
          {message && showError && !isFocusedChooseSignup && (
            <ErrorMessageWithDefaultClassName
              isDisabled={isDisabled}
              fadeOnDisable={fadeOnDisable}
              popOverError={popOverError}
              onClick={handleSignInUserAccount}
            >
              <SanitizedFormattedHtmlMessage
                {...getTranslatableError(message)}
                values={{ signInAccountUrl }}
              />
            </ErrorMessageWithDefaultClassName>
          )}

          {isValid && !isCheckingSSO && ssoRedirectUrl && (
            <div onClick={emailExistsOrSSOAnalytics}>
              <ErrorMessageWithDefaultClassName>
                {buildErrorMessage(ssoRedirectUrl)}
              </ErrorMessageWithDefaultClassName>
            </div>
          )}
        </ChooseSignupSuccessTextfieldWrapper>
      ) : (
        <SuccessTextfieldWrapper
          textInput={email}
          isValid={!!isValid && !isUserExists && !isCheckingSSO}
          hasAccount={initialEmail !== ''}
          isEmpty={email.length === 0}
          isFocused={isFocused}
          isDisabled={isDisabled}
          fadeOnDisable={fadeOnDisable}
          outlineOnFocus={outlineOnFocus}
        >
          <Textfield
            className="email-field input"
            id="email-field-input"
            testId="email_field"
            isRequired
            value={email}
            isInvalid={
              isCheckingSSO && isUserExists && isValid === false && email !== ''
            }
            style={{ height: 'inherit', color: '#091E44', padding: '8px 10px' }}
            autoCorrect="off"
            autoCapitalize="none"
            autoComplete="email"
            isDisabled={hasInitialEmail || isDisabled}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(event: any) => {
              setEmail(removeSpaces(event.currentTarget.value));
            }}
            elemAfterInput={
              <EmailAfter>
                {!isCheckingSSO &&
                !isUserExists &&
                initialEmail === '' &&
                isValid ? (
                  <CheckCircleOutlineIcon
                    testId="email_success"
                    label="email_success"
                    primaryColor="#36B37E"
                  />
                ) : (isValid === false && email !== '') || isUserExists ? (
                  showWarningIcon && (
                    <WarningIcon
                      testId="email_warning"
                      label="email_warning"
                      primaryColor="#DE350B"
                    />
                  )
                ) : isValidating || isCheckingSSO ? (
                  <Spinner size="small" />
                ) : null}
              </EmailAfter>
            }
          />
          {message && (
            <ErrorMessageWithDefaultClassName
              isDisabled={isDisabled}
              fadeOnDisable={fadeOnDisable}
              popOverError={popOverError}
              onClick={handleSignInUserAccount}
            >
              <SanitizedFormattedHtmlMessage
                {...getTranslatableError(message)}
                values={{ signInAccountUrl }}
              />
            </ErrorMessageWithDefaultClassName>
          )}

          {isValid && !isCheckingSSO && ssoRedirectUrl && (
            <div onClick={emailExistsOrSSOAnalytics}>
              <ErrorMessageWithDefaultClassName>
                {buildErrorMessage(ssoRedirectUrl)}
              </ErrorMessageWithDefaultClassName>
            </div>
          )}
        </SuccessTextfieldWrapper>
      )}
      {!!switchAccountUrl && hasInitialEmail && (
        <SigninWithDiffAccount onClick={handleSwitchUserAccount}>
          {isDeferredSiteNameEnabled ? (
            <SanitizedFormattedHtmlMessage
              {...messages.useDifferentAccountSignin}
              values={{ switchAccountUrl }}
            />
          ) : (
            <SanitizedFormattedHtmlMessage
              {...messages.differentAcountSignin}
              values={{ switchAccountUrl }}
            />
          )}
        </SigninWithDiffAccount>
      )}
    </FieldContainer>
  );
}

export default injectIntl(EmailField);
